import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { LoadingOutlined } from '@ant-design/icons';

import * as services from '../../../redux/services';
import { fetchACEvent } from '../../../features/automated-campaigns/api/api';
import MemberMessagesDrawer from '../../MemberMessagesDrawer/MemberMessagesDrawer';
import {
  Section,
  Header,
  Title,
  TitleIcon,
  ViewAllButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
  ColLabel,
  CellContent,
  Subject,
  Dot,
  Dash,
  Empty,
} from './EngagementHistory.styled';

const RECENT_MESSAGE_LIMIT = 3;

const firstEventTime = (events, eventName) =>
  events
    .filter(event => event.eventName === eventName)
    .reduce(
      (earliest, event) =>
        earliest == null || event.time < earliest ? event.time : earliest,
      null
    );

const formatStamp = stamp =>
  typeof stamp === 'number' ? moment.unix(stamp).format('MM/DD/YY') : stamp;

const EngagementHistory = ({
  noteRouterId,
  orgId,
  contactInfo,
  glow = false,
}) => {
  const [recentEngagementMessages, setRecentEngagementMessages] = useState([]);
  const [recentMessagesLoading, setRecentMessagesLoading] = useState(true);
  const [fullEngagementMessages, setFullEngagementMessages] = useState([]);
  const [fullMessagesFetched, setFullMessagesFetched] = useState(false);
  const [fullMessagesLoading, setFullMessagesLoading] = useState(false);
  const [firstEventTimesByMessageId, setFirstEventTimesByMessageId] = useState(
    {}
  );
  const [messagesDrawerVisible, setMessagesDrawerVisible] = useState(false);
  const [hasOpenedFullDrawer, setHasOpenedFullDrawer] = useState(false);
  const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState({});
  const [selectedMessageEngagement, setSelectedMessageEngagement] = useState({
    events: [],
  });
  const [messageDetailsLoading, setMessageDetailsLoading] = useState(false);

  useEffect(() => {
    if (!noteRouterId || !orgId) return undefined;
    let cancelled = false;
    setRecentMessagesLoading(true);
    setFullEngagementMessages([]);
    setFullMessagesFetched(false);
    setHasOpenedFullDrawer(false);
    setMessagesDrawerVisible(false);
    setDetailsDrawerVisible(false);
    setSelectedMessage({});
    setSelectedMessageEngagement({ events: [] });

    (async () => {
      try {
        const fetchedMessages = await services.getRecentEngagementMessages(
          noteRouterId,
          orgId,
          RECENT_MESSAGE_LIMIT
        );
        if (cancelled) return;

        const messages = Array.isArray(fetchedMessages) ? fetchedMessages : [];
        messages.forEach(message => {
          message.date = moment
            .unix(message.sendTime)
            .format('YYYY-MM-DD hh:mm A');
        });
        setRecentEngagementMessages(messages);

        if (messages.length === 0) {
          setFirstEventTimesByMessageId({});
          return;
        }

        const firstEventTimeEntries = await Promise.all(
          messages.map(async message => {
            const messageEventInfo = message.isAC
              ? await fetchACEvent(message.sgCompleteId)
              : await services.getMemberEventInfo(
                  message.id,
                  noteRouterId,
                  orgId
                );
            const events = messageEventInfo?.memberEngagement?.events || [];
            return [
              message.id,
              {
                firstOpen: firstEventTime(events, 'open'),
                firstClick: firstEventTime(events, 'click'),
              },
            ];
          })
        );
        if (cancelled) return;

        setFirstEventTimesByMessageId(
          Object.fromEntries(firstEventTimeEntries)
        );
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load engagement history', err);
          setRecentEngagementMessages([]);
          setFirstEventTimesByMessageId({});
        }
      } finally {
        if (!cancelled) setRecentMessagesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [noteRouterId, orgId]);

  const openFullHistoryDrawer = async () => {
    setHasOpenedFullDrawer(true);
    setMessagesDrawerVisible(true);
    if (fullMessagesFetched || fullMessagesLoading) return;
    setFullMessagesLoading(true);
    try {
      const fetchedMessages = await services.getEngagementMessages(
        noteRouterId,
        orgId
      );
      const messages = Array.isArray(fetchedMessages) ? fetchedMessages : [];
      messages.forEach(message => {
        message.date = moment
          .unix(message.sendTime)
          .format('YYYY-MM-DD hh:mm A');
      });
      setFullEngagementMessages(messages);
      setFullMessagesFetched(true);
    } catch (err) {
      console.error('Failed to load full engagement history', err);
      setFullEngagementMessages([]);
    } finally {
      setFullMessagesLoading(false);
    }
  };

  const showEngagementDetailsDrawer = async message => {
    setDetailsDrawerVisible(true);
    setSelectedMessageEngagement({ events: [] });
    setMessageDetailsLoading(true);
    const messageEventInfo = message?.isAC
      ? await fetchACEvent(message.sgCompleteId)
      : await services.getMemberEventInfo(
          message.id,
          noteRouterId,
          orgId,
          message
        );
    if (messageEventInfo?.messageInfo) {
      const { messageInfo } = messageEventInfo;
      const deliveryDate =
        typeof messageInfo.deliveryDate === 'number'
          ? moment.unix(messageInfo.deliveryDate).format('MM/DD/YY hh:mm A')
          : messageInfo.deliveryDate;
      const enriched = {
        ...message,
        ...(message?.isAC && {
          stageName: messageInfo.stageName,
          acName: messageInfo.acName,
          stageId: messageInfo.stageId,
          acId: messageInfo.acId,
        }),
        memberEmail: messageInfo.memberEmail,
        memberPhone: messageInfo.memberPhone,
        deliveryDate,
        orgPhone: messageInfo.orgPhone,
        from: messageInfo.from,
      };
      setSelectedMessage(enriched);
      setSelectedMessageEngagement(messageEventInfo.memberEngagement);
    } else {
      setSelectedMessage(message);
      setSelectedMessageEngagement({ events: [] });
    }
    setMessageDetailsLoading(false);
  };

  return (
    <Section $glow={glow}>
      <Header>
        <Title>
          <TitleIcon />
          Engagement History
        </Title>
        <ViewAllButton type="button" onClick={openFullHistoryDrawer}>
          View Full History <span aria-hidden="true">&rarr;</span>
        </ViewAllButton>
      </Header>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>
              <ColLabel>DATE SENT</ColLabel>
            </TableHeaderCell>
            <TableHeaderCell>
              <ColLabel>SUBJECT</ColLabel>
            </TableHeaderCell>
            <TableHeaderCell>
              <ColLabel>OPENED</ColLabel>
            </TableHeaderCell>
            <TableHeaderCell>
              <ColLabel>CLICKED</ColLabel>
            </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {recentMessagesLoading && (
            <TableRow>
              <TableCell colSpan={4}>
                <Empty>
                  <LoadingOutlined /> Loading...
                </Empty>
              </TableCell>
            </TableRow>
          )}
          {!recentMessagesLoading && recentEngagementMessages.length === 0 && (
            <TableRow>
              <TableCell colSpan={4}>
                <Empty>No messages sent yet.</Empty>
              </TableCell>
            </TableRow>
          )}
          {!recentMessagesLoading &&
            recentEngagementMessages.map((message, index) => {
              const sentDate = message.sendTime
                ? moment.unix(message.sendTime).format('MM/DD/YY')
                : '--';
              const engagementDetail =
                firstEventTimesByMessageId[message.id] || {};
              const firstOpenedAt = engagementDetail.firstOpen;
              const firstClickedAt = engagementDetail.firstClick;
              return (
                <TableRow key={message.id || index}>
                  <TableCell>
                    <CellContent>{sentDate}</CellContent>
                  </TableCell>
                  <TableCell>
                    <Subject title={message.messageSubject}>
                      {message.messageSubject || '--'}
                    </Subject>
                  </TableCell>
                  <TableCell>
                    <CellContent>
                      <Dot $on={!!firstOpenedAt} />
                      {firstOpenedAt ? (
                        <>Opened &middot; {formatStamp(firstOpenedAt)}</>
                      ) : (
                        <Dash>&mdash;</Dash>
                      )}
                    </CellContent>
                  </TableCell>
                  <TableCell>
                    <CellContent>
                      <Dot $on={!!firstClickedAt} />
                      {firstClickedAt ? (
                        <>Clicked &middot; {formatStamp(firstClickedAt)}</>
                      ) : (
                        <Dash>&mdash;</Dash>
                      )}
                    </CellContent>
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>
      {hasOpenedFullDrawer && (
        <MemberMessagesDrawer
          setVisible={setMessagesDrawerVisible}
          visible={messagesDrawerVisible}
          messages={fullEngagementMessages}
          msgsLoading={fullMessagesLoading}
          setDetailsDrawerVisible={setDetailsDrawerVisible}
          detailsDrawerVisible={detailsDrawerVisible}
          showdetailsDrawer={showEngagementDetailsDrawer}
          memberMessageInfo={selectedMessage}
          memberInfo={contactInfo}
          msgDetailsLoading={messageDetailsLoading}
          memberEngagement={selectedMessageEngagement}
        />
      )}
    </Section>
  );
};

export default EngagementHistory;
